/* eslint-disable react/prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Track from './Track'
import createSlider from './createSlider'
import * as utils from './utils'
import Handle from './Handle'
import isEqual from 'lodash/isEqual'

class Slider extends React.Component {
  static displayName = 'Slider'
  static propTypes = {
    /** 最小值 */
    min: PropTypes.number,
    /** 最大值 */
    max: PropTypes.number,
    /** 步长 */
    step: PropTypes.number,
    /** 默认值 */
    defaultValue: PropTypes.arrayOf(PropTypes.number),
    /** change回调 */
    onChange: PropTypes.func,
    /** 是否允许较差 */
    allowCross: PropTypes.bool,
    /** 是否禁用 */
    disabled: PropTypes.bool,
    /** 自定义点 */
    handle: PropTypes.func
  }

  static defaultProps = {
    count: 1,
    allowCross: false
  }

  constructor(props) {
    super(props)

    const { count, min, max } = props
    const initialValue = Array.apply(null, Array(count + 1)).map(() => min)
    const defaultValue =
      'defaultValue' in props ? props.defaultValue : initialValue
    const value = props.value !== undefined ? props.value : defaultValue
    const bounds = value.map((v, i) => this.trimAlignValue(v, i))
    const recent = bounds[0] === max ? 0 : bounds.length - 1

    this.state = {
      handle: null,
      recent,
      bounds
    }
  }

  componentWillReceiveProps(nextProps) {
    if (!('value' in nextProps || 'min' in nextProps || 'max' in nextProps)) {
      return
    }
    if (
      this.props.min === nextProps.min &&
      this.props.max === nextProps.max &&
      isEqual(this.props.value, nextProps.value)
    ) {
      return
    }

    const { bounds } = this.state
    const value = nextProps.value || bounds
    const nextBounds = value.map((v, i) => this.trimAlignValue(v, i, nextProps))
    if (
      nextBounds.length === bounds.length &&
      nextBounds.every((v, i) => v === bounds[i])
    ) {
      return
    }

    this.setState({ bounds: nextBounds })

    if (bounds.some(v => utils.isValueOutOfRange(v, nextProps))) {
      const newValues = value.map(v => {
        return utils.ensureValueInRange(v, nextProps)
      })
      this.props.onChange(newValues)
    }
  }

  onChange(state) {
    const props = this.props
    const isNotControlled = !('value' in props)
    if (isNotControlled) {
      this.setState(state)
    } else if (state.handle !== undefined) {
      this.setState({ handle: state.handle })
    }

    const data = { ...this.state, ...state }
    const changedValue = data.bounds
    props.onChange(changedValue)
  }

  onStart(position) {
    const state = this.state
    const bounds = this.getValue()

    const value = this.calcValueByPos(position)
    this.startValue = value
    this.startPosition = position
    this.startBounds = bounds

    // 若点击中间，则可以拖动整体
    if (bounds[0] < value - 1 && bounds[1] > value + 1) {
      this.moveAll = true
      return
    } else {
      this.moveAll = false
    }

    const closestBound = this.getClosestBound(value)
    this.prevMovedHandleIndex = this.getBoundNeedMoving(value, closestBound)

    this.setState({
      handle: this.prevMovedHandleIndex,
      recent: this.prevMovedHandleIndex
    })

    const prevValue = bounds[this.prevMovedHandleIndex]
    if (value === prevValue) return

    const nextBounds = [...state.bounds]
    nextBounds[this.prevMovedHandleIndex] = value
    this.onChange({ bounds: nextBounds })
  }

  onEnd = () => {
    this.removeDocumentEvents()
    this.moveAll = false
  }

  onMove(e, position) {
    utils.pauseEvent(e)
    const state = this.state
    // 统一移动
    if (this.moveAll) {
      const bounds = this.startBounds
      const change = position - this.startPosition
      const newStartPos = this.calcPosByValue(bounds[0]) + change
      const newEndPos = this.calcPosByValue(bounds[1]) + change
      const newBounds = [
        this.calcValueByPos(newStartPos),
        this.calcValueByPos(newEndPos)
      ]
      if (bounds[0] === newBounds[0]) return
      this.moveTo(newBounds)
      return
    }
    const value = this.calcValueByPos(position)
    const oldValue = state.bounds[state.handle]
    if (value === oldValue) return

    this.moveTo(value)
  }

  getValue() {
    return this.state.bounds
  }

  getClosestBound(value) {
    const { bounds } = this.state
    let closestBound = 0
    for (let i = 1; i < bounds.length - 1; ++i) {
      if (value > bounds[i]) {
        closestBound = i
      }
    }
    if (
      Math.abs(bounds[closestBound + 1] - value) <
      Math.abs(bounds[closestBound] - value)
    ) {
      closestBound = closestBound + 1
    }
    return closestBound
  }

  getBoundNeedMoving(value, closestBound) {
    const { bounds, recent } = this.state
    let boundNeedMoving = closestBound
    const isAtTheSamePoint = bounds[closestBound + 1] === bounds[closestBound]

    if (isAtTheSamePoint && bounds[recent] === bounds[closestBound]) {
      boundNeedMoving = recent
    }

    if (isAtTheSamePoint && value !== bounds[closestBound + 1]) {
      boundNeedMoving =
        value < bounds[closestBound + 1] ? closestBound : closestBound + 1
    }
    return boundNeedMoving
  }

  getLowerBound() {
    return this.state.bounds[0]
  }

  getUpperBound() {
    const { bounds } = this.state
    return bounds[bounds.length - 1]
  }

  /**
   * Returns an array of possible slider points, taking into account both
   * `marks` and `step`. The result is cached.
   */
  getPoints() {
    const { marks, step, min, max } = this.props
    const cache = this._getPointsCache
    if (!cache || cache.marks !== marks || cache.step !== step) {
      const pointsObject = { ...marks }
      if (step !== null) {
        for (let point = min; point <= max; point += step) {
          pointsObject[point] = point
        }
      }
      const points = Object.keys(pointsObject).map(parseFloat)
      points.sort((a, b) => a - b)
      this._getPointsCache = { marks, step, points }
    }
    return this._getPointsCache.points
  }

  moveTo(value) {
    const { state, props } = this
    let nextBounds = [...state.bounds]

    // 整体移动
    if (this.moveAll) {
      return this.onChange({
        handle: 'all',
        bounds: [...value].sort((a, b) => a - b)
      })
    }

    nextBounds[state.handle] = value
    let nextHandle = state.handle
    if (props.allowCross) {
      nextBounds.sort((a, b) => a - b)
      nextHandle = nextBounds.indexOf(value)
    }
    this.onChange({
      handle: nextHandle,
      bounds: nextBounds
    })
  }

  trimAlignValue(v, handle, nextProps = {}) {
    const mergedProps = { ...this.props, ...nextProps }
    const valInRange = utils.ensureValueInRange(v, mergedProps)
    const valNotConflict = this.ensureValueNotConflict(
      handle,
      valInRange,
      mergedProps
    )
    return utils.ensureValuePrecision(valNotConflict, mergedProps)
  }

  ensureValueNotConflict(handle, val, { allowCross, pushable: thershold }) {
    const state = this.state || {}
    const { bounds } = state
    handle = handle === undefined ? state.handle : handle
    thershold = Number(thershold)
    /* eslint-disable eqeqeq */
    if (!allowCross && handle != null && bounds !== undefined) {
      if (handle > 0 && val <= bounds[handle - 1] + thershold) {
        return bounds[handle - 1] + thershold
      }
      if (handle < bounds.length - 1 && val >= bounds[handle + 1] - thershold) {
        return bounds[handle + 1] - thershold
      }
    }
    /* eslint-enable eqeqeq */
    return val
  }

  render() {
    const { handle, bounds } = this.state
    const {
      prefixCls,
      vertical,
      included,
      disabled,
      min,
      max,
      handle: handleGenerator
    } = this.props

    const offsets = bounds.map(v => this.calcOffset(v))

    const handleClassName = `${prefixCls}-handle`
    const handles = bounds.map((v, i) =>
      handleGenerator({
        className: classNames({
          [handleClassName]: true,
          [`${handleClassName}-${i + 1}`]: true
        }),
        prefixCls,
        vertical,
        offset: offsets[i],
        value: v,
        dragging: handle === i,
        index: i,
        tabIndex: 0,
        min,
        max,
        disabled,
        ref: h => this.saveHandle(i, h)
      })
    )

    const tracks = bounds.slice(0, -1).map((_, index) => {
      const i = index + 1
      const trackClassName = classNames({
        [`${prefixCls}-track`]: true,
        [`${prefixCls}-track-${i}`]: true
      })
      return (
        <Track
          className={trackClassName}
          vertical={vertical}
          included={included}
          offset={offsets[i - 1]}
          length={offsets[i] - offsets[i - 1]}
          key={i}
        />
      )
    })

    return { tracks, handles }
  }
}

const d = createSlider(Slider)
d.Handle = Handle

export default d
